import React from 'react';
import {
  ConversationListArea,
  ConversationListHeader,
  ScrollableContent,
  NoResultsMessage,
  HeaderIconButton,
  Overlay,
} from '../../styles/messenger/layout.style';
import {
  ConversationItem,
  Avatar,
  ProfileInitial,
  ConversationInfo,
  ConversationName,
  LastMessage,
  NameAndMessage,
  MessageTimestamp,
  NameAndTagsContainer,
  MessageMetaRow,
  TagWrap,
  Tag
} from '../../styles/messenger/messenger.style';
import { ActionButton } from '../../styles/profile/profile.style';

import { PencilFill, XCircleFill } from 'react-bootstrap-icons';
import { Trash2 } from 'lucide-react';
import { ClipLoader } from 'react-spinners';

import { Connection } from '../../hooks/useConnections';
import { useConnectionTags } from '../../hooks/useConnectionTags';
import { useSortedConnections } from '../../hooks/useSortedConnections';
import { CustomCheckbox } from './CustomCheckbox';
import { getFirstName } from '../../utils/name';
import { formatRelativeTime } from '../../utils/formatDate';
import { TuteeLoader } from '../common/TuteeLoader';
import { getInitial as getInitials } from '../../utils/name';

const ADMIN_USER_ID = process.env.REACT_APP_ADMIN_USER_ID;

type UserRole = 'parent' | 'tutor' | string;

interface ConnectionListProps {
    connections: Connection[];
    isLoading: boolean;
    currentUserId?: string;
    currentUserRole?: UserRole;
    activeConnectionId: string | null;
    isEditing: boolean;
    selectedConnections: Set<string>;
    onToggleEdit: () => void;
    onSelectionChange: (connectionId: string) => void;
    onSelectConnection: (connectionId: string) => void;
    onRemoveClick: () => void;
}

const isAdminConnection = (connection: Connection): boolean => {
  if (!ADMIN_USER_ID) return false;
  return connection.parentId === ADMIN_USER_ID || connection.tutorId === ADMIN_USER_ID;
};

export const ConnectionList = ({
    connections,
    isLoading,
    currentUserId,
    currentUserRole,
    activeConnectionId,
    isEditing,
    selectedConnections,
    onToggleEdit,
    onSelectionChange,
    onSelectConnection,
    onRemoveClick,
}: ConnectionListProps) => {
    const sortedConnections = useSortedConnections(connections);
    const {shouldRenderTags} = useConnectionTags(sortedConnections);
    const showNoResults = !isLoading && sortedConnections.length === 0;

    return (
        <ConversationListArea>
          {showNoResults ? (
            <ConversationListHeader />
          ) : isEditing ? (
            <ConversationListHeader>
              <ActionButton
                intent="background"
                onClick={onRemoveClick}
                disabled={selectedConnections.size === 0}
              >
                <Trash2 size={18} stroke="red" />
              </ActionButton>
              <HeaderIconButton onClick={onToggleEdit}>
                <XCircleFill size={18} />
              </HeaderIconButton>
            </ConversationListHeader>
          ) : (
            <ConversationListHeader>
              Connections
              <HeaderIconButton onClick={onToggleEdit}>
                <PencilFill size={18} />
              </HeaderIconButton>
            </ConversationListHeader>
          )}
    
          <ScrollableContent>
            {isLoading ? (
              <Overlay>
                <TuteeLoader />
                </Overlay>
            ) : showNoResults ? (
              <NoResultsMessage>
                <h2>No Connections Found</h2>
                <p>New connections will appear here.</p>
              </NoResultsMessage>
            ) : (
              sortedConnections.map(connection => {
                const otherUserInList =
                  currentUserId === connection.parentId ? connection.tutor : connection.parent;
    
                const tutor =
                  currentUserRole === 'parent' ? otherUserInList : connection.tutor;
                const parent =
                  currentUserRole === 'parent' ? connection.parent : otherUserInList;
    
                const parentSubjectsSet = new Set(parent?.subjects || []);
                const matchingSubjects =
                  tutor?.subjects?.filter(subject => parentSubjectsSet.has(subject)) || [];
    
                const isUnread = connection.hasUnreadMessages;
                const isSavedUnread = connection.status === 'saved';
                const makeBold = isUnread || isSavedUnread;
                const isAdmin = isAdminConnection(connection);
    
                let messagePreview = 'No messages yet';
                if (connection.lastMessageContent) {
                  const prefix =
                    connection.lastMessageSenderId === currentUserId ? 'You: ' : '';
                  messagePreview = `${prefix}${connection.lastMessageContent}`;
                }
    
                const timeAgo = formatRelativeTime(connection.lastMessageAt);
    
                return (
                  <ConversationItem
                    key={connection.id}
                    data-active={connection.id === activeConnectionId && !isEditing}
                    onClick={() => !isEditing && onSelectConnection(connection.id)}
                    css={{ alignItems: 'center' }}
                  >
                    {isEditing && (
                      <CustomCheckbox
                        checked={selectedConnections.has(connection.id)}
                        onChange={() => onSelectionChange(connection.id)}
                      />
                    )}
    
                    {otherUserInList.role === 'tutor' ? (
                      <Avatar
                        src={otherUserInList.profilePhotoUrl || '/default-avatar.png'}
                        alt={otherUserInList.name}
                      />
                    ) : (
                      <ProfileInitial
                        style={{ backgroundColor: otherUserInList.profileColor || '#ccc' }}
                      >
                        {getInitials(otherUserInList.name)}
                      </ProfileInitial>
                    )}
    
                      <ConversationInfo>
                      
                      {/* Row 1: Name and Tags */}
                      <NameAndTagsContainer>
                        <ConversationName isUnread={makeBold}>
                          {getFirstName(otherUserInList.name)}
                        </ConversationName>

                        {shouldRenderTags && matchingSubjects.length > 0 && (
                          <TagWrap>
                            {matchingSubjects.map((subject) => (
                              <Tag key={subject} isMatch>
                                {subject}
                              </Tag>
                            ))}
                          </TagWrap>
                        )}
                      </NameAndTagsContainer>

                      {/* Row 2: Message and Timestamp */}
                      <MessageMetaRow>
                        <LastMessage isUnread={makeBold}>{messagePreview}</LastMessage>
                        <MessageTimestamp>{timeAgo}</MessageTimestamp>
                      </MessageMetaRow>

                    </ConversationInfo>
                  </ConversationItem>
                );
              })
            )}
          </ScrollableContent>
        </ConversationListArea>
      );
    };